import { useState, useEffect, useRef } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import TruncatedTd from "../layout/TruncatedTd";
import GenerateDriverInvoice from "./GenerateDriverInvoice";
import Pagination from "../layout/Pagination";
import PermissionGate from "../layout/PermissionGate";
import { formatDecimal } from "../utils/decimalFormat";
import { truncateDecimal } from "../utils/truncateDecimal";
import normalize from "../utils/NormalizeSelect";

const DriverPerformance = ({ id }) => {
  const [data, setData] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [merchant, setMerchant] = useState({});
  const [branch, setBranch] = useState({});
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [search, setSearch] = useState("");
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const dateRangePickerRef = useRef(null);
  const token = localStorage.getItem("token");
  const limit = process.env.REACT_APP_PAGINATION_LIMIT;

  const fetchTransactions = () => {
    axios
      .get(
        `${process.env.REACT_APP_API_ENDPOINT
        }driver-invoices?page=${page}&limit=${limit}&merchantId=${merchant.value === undefined ? "" : merchant.value
        }&branchId=${branch.value === undefined ? "" : branch.value
        }&driverId=${id}&search=${search}&startDate=${start}&endDate=${end}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        if (res.data.code === 1) {
          if (page > 1) {
            if (res.data.data.length === 0) {
              setPage(page - 1);
            } else {
              setData(res.data.data);
              setCount(res.data.count);
            }
          } else {
            setData(res.data.data);
            setCount(res.data.count);
          }
          setTotalRevenue(res?.data?.totalAmount ?? 0);
          setTotalOrderCount(res?.data?.totalOrderCount ?? 0);
        }
      })
      .catch(console.error);
  };
  const fetchMerchantList = () => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}merchant-list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setMerchants(res.data.data);
        }
      })
      .catch(console.error);
  };
  const fetchBranchList = (merchantId) => {
    axios
      .get(`${process.env.REACT_APP_API_ENDPOINT}branch-list/${merchantId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (res.data.code === 1) {
          setBranches(res.data.data);
        }
      })
      .catch(console.error);
  };
  const reset = () => {
    setPage(1);
    setMerchant({});
    setBranch({});
    setBranches([]);
    setStart("");
    setEnd("");
    setSearch("");
  };
  const openCalendar = () => {
    if (dateRangePickerRef.current) {
      dateRangePickerRef.current.flatpickr.open();
    }
  };
  useEffect(() => {
    fetchTransactions();
    fetchMerchantList();
  }, []);
  useEffect(() => {
    fetchTransactions();
  }, [page, merchant, branch, start, end, search]);

  return (
    <div className="row g-5 g-lg-10 mt-4">
      <div className="col p-0 mb-xl-10">
        <div className="d-block d-sm-flex justify-content-between mb-5">
          <div>
            <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-3">
              Performance
            </h1>
          </div>
          <PermissionGate required={`view_driver_performance`} fallback={<></>}>
            <div className="d-flex justify-content-between gap-2">
              <button type="button" className="btn btn-success">
                Earnings: {formatDecimal(totalRevenue)}
              </button>
              <button type="button" className="btn btn-success">
                Total Trips: {totalOrderCount}
              </button>
              {/*begin::Button*/}
              <button
                onClick={reset}
                className="btn btn-color-danger bg-body mx-3"
              >
                Reset
              </button>
              {/*end::Button*/}
            </div>
          </PermissionGate>
        </div>
        {/*begin::Actions*/}
        <PermissionGate required={`view_driver_performance`} fallback={<></>}>
          <div className="row">
            <div className="col-sm-12 col-md mb-3">
              {/* begin::Merchant List */}
              <Select
                className="react-select-styled react-select-bordered"
                styles={{ width: "100%" }}
                classNamePrefix="react-select"
                value={merchant.value === undefined ? null : merchant}
                onChange={(e) => {
                  setMerchant(e);
                  fetchBranchList(e?.value);
                }}
                options={merchants?.map((item) => {
                  return {
                    value: item?._id,
                    label: item?.merchantName,
                  };
                })}
                filterOption={(option, input) => {
                  return normalize(option.label).includes(normalize(input));
                }}
                placeholder="Search Merchant"
              />
              {/* end::Merchant List */}
            </div>
            <div className="col-sm-12 col-md mb-3">
              {/* begin::Branch List */}
              <Select
                className="react-select-styled react-select-bordered"
                classNamePrefix="react-select"
                value={branch.value === undefined ? null : branch}
                onChange={(e) => {
                  setBranch(e);
                }}
                options={branches?.map((item) => {
                  return {
                    value: item?._id,
                    label: item?.name,
                  };
                })}
                filterOption={(option, input) => {
                  return normalize(option.label).includes(normalize(input));
                }}
                placeholder="Search Branch"
              />
              {/* end::Branch List */}
            </div>
            <div className="col-sm-12 col-md mb-3">
              <input
                type="text"
                value={search}
                autoComplete="off"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                className="form-control"
                placeholder="Order Id or Transaction Id"
              />
            </div>
            <div className="col-sm-12 col-md mb-3">
              {/*begin::Daterange*/}
              <div className="d-flex position-relative">
                <Flatpickr
                  ref={dateRangePickerRef}
                  value={
                    start !== "" && end !== ""
                      ? [new Date(start), new Date(end)]
                      : null
                  }
                  options={{ dateFormat: "d-m-Y", mode: "range" }}
                  onChange={([start, end]) => {
                    if (
                      start !== undefined &&
                      end !== undefined &&
                      typeof start === "object" &&
                      typeof end === "object"
                    ) {
                      setStart(moment(start).format("YYYY-MM-DD"));
                      setEnd(moment(end).format("YYYY-MM-DD"));
                    }
                  }}
                  className="form-control form-control-lg"
                  placeholder="Order date"
                />
                <i
                  onClick={openCalendar}
                  className="bi-duotone bi-calendar fs-5 position-absolute"
                  style={{ top: "28%", right: "5%", cursor: "pointer" }}
                ></i>
              </div>
              {/*end::Daterange*/}
            </div>
          </div>
        </PermissionGate>
        {/*end::Actions*/}
        <PermissionGate required={`view_driver_performance`}>
          <div className="card py-10">
            {/*begin::Card body*/}
            <div className="card-body py-4 text-center" id="filterDataWrap">
              {data?.length === 0 ? (
                <div className="text-center w-100">
                  <img
                    className="w-25 mx-auto py-20"
                    src={`${process.env.REACT_APP_PUBLIC_URL}assets/media/misc/no-content.png`}
                    alt="no-content"
                  />
                </div>
              ) : (
                <div className="table-responsive">
                  {/*begin::Table*/}
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-muted fw-bold fs-7 text-uppercase gs-0">
                        <th className="text-center min-w-100px">
                          Transaction Id
                        </th>
                        <th className="text-center min-w-100px">Merchant</th>
                        <th className="text-center min-w-100px">Branch</th>
                        <th className="text-center min-w-100px">Driver</th>
                        <th className="text-center min-w-100px">Order Id</th>
                        <th className="text-center min-w-100px">Type</th>
                        <th className="text-center min-w-100px">Notes</th>
                        <th className="text-center min-w-100px">Date</th>
                        <th className="text-center min-w-100px">Time</th>
                        <th className="text-center min-w-100px">Amount</th>
                        <th className="text-center min-w-100px">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 fw-semibold">
                      {data &&
                        data?.map((item, index) => {
                          return (
                            <tr className="text-center" key={index}>
                              <td>{item?.transactionId ?? "---"}</td>
                              <TruncatedTd length={8}>
                                {item?.merchantName ?? "---"}
                              </TruncatedTd>
                              <TruncatedTd length={8}>
                                {item?.branchName ?? "---"}
                              </TruncatedTd>
                              <TruncatedTd length={8}>
                                {item?.driverName ?? "---"}
                              </TruncatedTd>
                              <td>{item?.orderId ?? "---"}</td>
                              <TruncatedTd length={8}>
                                {item?.type?.toUpperCase()}
                              </TruncatedTd>
                              <TruncatedTd length={15}>
                                {item?.notes}
                              </TruncatedTd>
                              <td>
                                {moment(item?.orderDateTime)
                                  .tz(process.env.REACT_APP_TIMEZONE)
                                  .format("Do MMM, YYYY")}
                              </td>
                              <td>
                                {moment(item?.orderDateTime)
                                  .tz(process.env.REACT_APP_TIMEZONE)
                                  .format("hh:mm A")}
                              </td>
                              <td>
                                <span
                                  className={
                                    item?.type === "credit"
                                      ? "badge text-white bg-success px-2"
                                      : "badge text-white bg-danger px-2"
                                  }
                                >
                                  {item?.type === "credit"
                                    ? `+${truncateDecimal(formatDecimal(item?.amountInt))}`
                                    : `-${truncateDecimal(formatDecimal(item?.amountInt))}`}
                                </span>
                              </td>
                              <td className="">
                                <div className="btn-group">
                                  {/*begin::Print*/}
                                  <GenerateDriverInvoice
                                    orderId={item?.orderId}
                                    transactionId={item?._id}
                                  />
                                  {/*end::Print*/}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  {/*end::Table*/}
                </div>
              )}
              {/*begin::Paginate*/}
              {count && Math.ceil(count / limit) > 1 ? (
                <Pagination
                  page={page}
                  setPage={setPage}
                  count={count}
                  limit={limit}
                />
              ) : (
                ""
              )}
              {/*end::Paginate*/}
            </div>
            {/*end::Card body*/}
          </div>
        </PermissionGate>
      </div>
    </div>
  );
};

export default DriverPerformance;
